// `excel_capture_sheet` — pure half of the capture channel (AUDIT_SPEC §2.4, plan "Shared contracts").
// The executor reads the sheet page by page; this module splits the rows into `POST /agent-v2/captures`
// bodies under a JSON budget and builds the compact receipt the model sees. No Office.js.
import type { CaptureChunkBody, CaptureMeta, CaptureReceipt, CaptureRow, CaptureUploadResponse } from '../../types/audit';

/** Target size of one chunk body (JSON chars ≈ bytes); the last row of a chunk may overshoot slightly. */
export const CAPTURE_CHUNK_BUDGET = 400_000;
/** Rows read per Office.js round trip by the runner. */
export const CAPTURE_PAGE_ROWS = 300;
export const CAPTURE_NOTE = 'Analyse it with audit_analyse_capture / audit_verify_series (capture_id).';

export interface CaptureSource {
  workbook_id: string;
  sheet: string;
  fingerprint: string | null;
  meta: CaptureMeta;
  rows: CaptureRow[];
}

/** Split a capture into chunk bodies of at most ~`budget` JSON chars each (always ≥ 1 row per chunk,
 *  always ≥ 1 chunk). Chunk 0 carries `meta`; `capture_id` stays null for the runner to fill in. */
export function chunkCapture(src: CaptureSource, budget = CAPTURE_CHUNK_BUDGET): CaptureChunkBody[] {
  const head = (index: number): CaptureChunkBody => ({
    workbook_id: src.workbook_id,
    sheet: src.sheet,
    fingerprint: src.fingerprint,
    capture_id: null,
    chunk_index: index,
    chunk_count: 0,
    meta: index === 0 ? src.meta : null,
    rows: [],
  });
  const chunks: CaptureChunkBody[] = [head(0)];
  let size = JSON.stringify(chunks[0]).length;
  for (const row of src.rows) {
    const rowSize = JSON.stringify(row).length + 1;
    const current = chunks[chunks.length - 1];
    if (current.rows.length > 0 && size + rowSize > budget) {
      const next = head(chunks.length);
      chunks.push(next);
      size = JSON.stringify(next).length;
    }
    chunks[chunks.length - 1].rows.push(row);
    size += rowSize;
  }
  for (const c of chunks) c.chunk_count = chunks.length;
  return chunks;
}

/** Cells and formula cells of a row set (fallback when the server response lacks them). */
export function countCells(rows: readonly CaptureRow[]): { cells: number; formula_cells: number } {
  let cells = 0;
  let formulas = 0;
  for (const r of rows) {
    cells += r.values.length;
    for (const f of r.formulas) if (typeof f === 'string' && f.startsWith('=')) formulas++;
  }
  return { cells, formula_cells: formulas };
}

/** The receipt of `excel_capture_sheet`: the server's counts when present, the local ones otherwise. */
export function buildReceipt(meta: CaptureMeta, upload: CaptureUploadResponse, chunks: readonly CaptureChunkBody[]): CaptureReceipt {
  const local = countCells(chunks.flatMap((c) => c.rows));
  const rows = chunks.reduce((n, c) => n + c.rows.length, 0);
  return {
    status: 'ok',
    capture_id: upload.capture_id,
    sheet: chunks[0]?.sheet ?? upload.sheet,
    fingerprint: upload.fingerprint ?? chunks[0]?.fingerprint ?? null,
    used_range: meta.used_range,
    rows: Number.isFinite(upload.rows) ? upload.rows : rows,
    cols: meta.cols,
    cells: Number.isFinite(upload.cells) ? upload.cells : local.cells,
    formula_cells: Number.isFinite(upload.formula_cells) ? upload.formula_cells : local.formula_cells,
    hidden_rows: meta.hidden_rows,
    hidden_cols: meta.hidden_cols,
    merged_count: meta.merged_areas.length,
    chunks: chunks.length,
    note: CAPTURE_NOTE,
  };
}
